import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { LogOut, Stethoscope } from "lucide-react";
import NotificationBell from "./NotificationBell";

const ROLE_LABEL = {
  patient: "Patient",
  doctor: "Doctor",
  clinic: "Clinic",
  admin: "Admin",
};

const PATIENT_LINKS = [
  { to: "/patient/dashboard", label: "Home" },
  { to: "/patient/doctors", label: "Doctors" },
  { to: "/patient/appointments", label: "Appointments" },
  { to: "/patient/medical-records", label: "Records" },
];

const DOCTOR_LINKS = [
  { to: "/doctor/dashboard", label: "Home" },
  { to: "/doctor/appointments", label: "Appointments" },
  { to: "/doctor/availability", label: "Availability" },
];

const CLINIC_LINKS = [
  { to: "/clinic/dashboard", label: "Home" },
  { to: "/clinic/appointments", label: "Appointments" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  const displayName = user && (user.name || user.phone || user.email || "User");
  const initials = (displayName || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const homePath = user && user.role ? `/${user.role}/dashboard` : "/";

  return (
    <header
      data-testid="app-navbar"
      className="glass sticky top-0 z-50 w-full border-b border-border h-16 flex items-center justify-between px-6 lg:px-10"
    >
      <div className="flex items-center gap-8 min-w-0">
        <Link to={homePath} data-testid="navbar-brand-link" className="flex items-center gap-2 shrink-0">
          <span className="h-9 w-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <Stethoscope className="h-5 w-5" />
          </span>
          <span className="font-semibold text-lg tracking-tight">
            Healthink<span className="text-primary">+</span>
          </span>
        </Link>

        {user && user.role === "patient" && (
          <nav className="hidden md:flex items-center gap-1" data-testid="navbar-patient-nav">
            {PATIENT_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  "px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors " +
                  (isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary")
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        )}
        {user && user.role === "doctor" && (
          <nav className="hidden md:flex items-center gap-1" data-testid="navbar-doctor-nav">
            {DOCTOR_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-doctor-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  "px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors " +
                  (isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary")
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        )}
        {user && user.role === "clinic" && (
          <nav className="hidden md:flex items-center gap-1" data-testid="navbar-clinic-nav">
            {CLINIC_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-clinic-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  "px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors " +
                  (isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary")
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        )}
      </div>

      <div className="flex items-center gap-3">
        {user && user !== false && <NotificationBell />}
        {user && user !== false && (
          <div className="hidden sm:flex items-center gap-3">
            <div className="text-right leading-tight">
              <div data-testid="navbar-user-name" className="text-sm font-medium">
                {displayName}
              </div>
              <div data-testid="navbar-user-role" className="text-xs text-muted-foreground">
                {ROLE_LABEL[user.role] || user.role}
              </div>
            </div>
            <div
              data-testid="navbar-user-avatar"
              className="h-9 w-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-sm font-semibold"
            >
              {initials}
            </div>
          </div>
        )}
        {user && user !== false && (
          <Button
            data-testid="navbar-logout-btn"
            variant="outline"
            size="sm"
            onClick={onLogout}
            className="rounded-full"
          >
            <LogOut className="h-4 w-4 mr-1.5" />
            Logout
          </Button>
        )}
      </div>
    </header>
  );
}
